import type {
  ConversationDto,
  ConversationListContactStateName,
  ConversationListItemDto,
  ConversationListSortName,
  ConversationListStatusName,
  ConversationListViewName,
  ConversationStatusName,
  PlatformTypeName,
} from "@multi-chat/shared";
import {
  DEFAULT_CONVERSATION_LIST_SORT,
  DEFAULT_CONVERSATION_LIST_STATUS,
  DEFAULT_CONVERSATION_LIST_VIEW,
} from "@multi-chat/shared";
import { normalizeConversationSearch } from "../../api/conversation-list-query.js";

export const CONVERSATION_SEARCH_DEBOUNCE_MS = 280;

export type ConversationListViewKind =
  | "loading"
  | "empty"
  | "searchEmpty"
  | "filterEmpty"
  | "searchFilterEmpty"
  | "ready"
  | "error";

export type ConversationListFetchMode = "load" | "refresh" | "merge";

export interface ConversationListQueryState {
  search?: string;
  platform?: PlatformTypeName;
  unread?: boolean;
  contactState?: ConversationListContactStateName;
  sort?: ConversationListSortName;
  view?: ConversationListViewName;
  status?: ConversationListStatusName;
}

export function conversationListQueryKey(
  organizationId: number,
  query: ConversationListQueryState,
): string {
  return [
    organizationId,
    normalizeConversationSearch(query.search) ?? "",
    query.platform ?? "",
    query.unread === true ? "unread" : "",
    query.contactState ?? "",
    query.sort ?? DEFAULT_CONVERSATION_LIST_SORT,
    query.view ?? DEFAULT_CONVERSATION_LIST_VIEW,
    query.status ?? DEFAULT_CONVERSATION_LIST_STATUS,
  ].join(":");
}

export function conversationListFetchMode(input: {
  previousKey: string | null;
  nextKey: string;
  previousKind: ConversationListViewKind;
}): ConversationListFetchMode {
  if (input.previousKey === input.nextKey && input.previousKind === "ready") {
    return "merge";
  }
  if (input.previousKind === "ready" || input.previousKind === "searchEmpty") {
    return "refresh";
  }
  return "load";
}

/**
 * Rescope only when the workspace scope version differs from the last one this
 * Inbox instance observed (starting with the version present at mount).
 */
export function shouldRescopeInbox(observedVersion: number, currentVersion: number): boolean {
  return observedVersion !== currentVersion;
}

/**
 * An events (SSE) connection opening while the first load for the same query is
 * still in flight must not abort and replace it; once loaded, refresh as usual.
 */
export function shouldRefreshListOnEventsOpen(input: {
  initialLoadQueryKey: string | null;
  currentQueryKey: string;
}): boolean {
  return input.initialLoadQueryKey !== input.currentQueryKey;
}

export function conversationListEmptyKind(
  query: ConversationListQueryState,
): "empty" | "searchEmpty" | "filterEmpty" | "searchFilterEmpty" {
  const hasSearch = normalizeConversationSearch(query.search) != null;
  const hasFilters =
    query.platform != null ||
    query.unread === true ||
    query.contactState != null ||
    (query.view != null && query.view !== "ALL") ||
    (query.status != null && query.status !== "ALL");
  if (hasSearch && hasFilters) return "searchFilterEmpty";
  if (hasSearch) return "searchEmpty";
  if (hasFilters) return "filterEmpty";
  return "empty";
}

export function isStaleConversationListResponse(input: {
  aborted: boolean;
  requestKey: string;
  currentKey: string;
}): boolean {
  return input.aborted || input.requestKey !== input.currentKey;
}

export function resolveSelectedConversation(
  selectedId: number | null,
  items: readonly ConversationListItemDto[] | undefined,
  snapshot: ConversationListItemDto | null,
): ConversationListItemDto | null {
  if (selectedId == null) {
    return null;
  }
  const fromList = items?.find((item) => item.conversationId === selectedId);
  if (fromList) {
    return fromList;
  }
  if (snapshot?.conversationId === selectedId) {
    return snapshot;
  }
  return null;
}

export function nextSelectedConversationSnapshot(
  selectedId: number | null,
  items: readonly ConversationListItemDto[],
  previous: ConversationListItemDto | null,
): ConversationListItemDto | null {
  if (selectedId == null) {
    return null;
  }
  const fromList = items.find((item) => item.conversationId === selectedId);
  if (fromList) {
    return fromList;
  }
  if (previous?.conversationId === selectedId) {
    return previous;
  }
  return null;
}

/** Keep an off-page deep-linked selection visible when the first list page loads. */
export function withDeepLinkedConversation(
  items: readonly ConversationListItemDto[],
  selectedId: number | null,
  snapshot: ConversationListItemDto | null,
): ConversationListItemDto[] {
  if (selectedId == null || snapshot == null || snapshot.conversationId !== selectedId) {
    return [...items];
  }
  if (items.some((item) => item.conversationId === selectedId)) {
    return [...items];
  }
  return [snapshot, ...items];
}

export function patchSelectedSnapshotFromDetails(
  snapshot: ConversationListItemDto | null,
  details: ConversationDto,
): ConversationListItemDto | null {
  if (!snapshot || snapshot.conversationId !== details.conversationId) {
    return snapshot;
  }
  return {
    ...snapshot,
    platformType: details.platformType,
    status: details.status,
    assignedMemberId: details.assignedMemberId,
    assignedMember: details.assignedMember,
    closedAt: details.closedAt,
    contactPlatformProfile: details.contactPlatformProfile,
  };
}

export function patchSelectedSnapshotUnread(
  snapshot: ConversationListItemDto | null,
  conversationId: number,
  unreadCount: number,
): ConversationListItemDto | null {
  if (!snapshot || snapshot.conversationId !== conversationId) {
    return snapshot;
  }
  return { ...snapshot, unreadCount };
}

export function reconcileReadSuccessInList(
  items: ConversationListItemDto[],
  conversationId: number,
  unreadCount: number,
  unreadFilterActive: boolean,
): ConversationListItemDto[] {
  const patched = items.map((item) =>
    item.conversationId === conversationId ? { ...item, unreadCount } : item,
  );
  if (!unreadFilterActive || unreadCount > 0) {
    return patched;
  }
  return patched.filter((item) => item.conversationId !== conversationId);
}

export function patchSelectedSnapshotProfile(
  snapshot: ConversationListItemDto | null,
  conversationId: number,
  contactPlatformProfile: ConversationListItemDto["contactPlatformProfile"],
): ConversationListItemDto | null {
  if (!snapshot || snapshot.conversationId !== conversationId) {
    return snapshot;
  }
  return { ...snapshot, contactPlatformProfile };
}

/** Build a list-row snapshot from a details DTO (deep-link when row is off-page). */
export function conversationSnapshotFromDetails(
  details: ConversationDto,
): ConversationListItemDto {
  return {
    conversationId: details.conversationId,
    contactPlatformProfileId: details.contactPlatformProfileId,
    platformEndpointId: details.platformEndpointId,
    platformType: details.platformType,
    status: details.status,
    assignedMemberId: details.assignedMemberId,
    assignedMember: details.assignedMember,
    closedAt: details.closedAt,
    contactPlatformProfile: details.contactPlatformProfile,
    lastMessageAt: details.lastInboundCustomerMessageAt,
    lastMessagePreview: null,
    unreadCount: 0,
  };
}

/** Authoritative fields from a confirmed mutation; overlay onto older list/detail responses. */
export type ConversationMutationOverlay = {
  epoch: number;
  unreadCount?: number;
  status?: ConversationListItemDto["status"];
  assignedMemberId?: number | null;
  assignedMember?: ConversationListItemDto["assignedMember"];
};

export function applyConversationMutationOverlay(
  item: ConversationListItemDto,
  overlay: ConversationMutationOverlay | undefined,
  responseEpoch: number,
): ConversationListItemDto {
  if (!overlay || overlay.epoch <= responseEpoch) {
    return item;
  }
  return {
    ...item,
    ...(overlay.unreadCount !== undefined ? { unreadCount: overlay.unreadCount } : {}),
    ...(overlay.status !== undefined ? { status: overlay.status } : {}),
    ...(overlay.assignedMemberId !== undefined
      ? { assignedMemberId: overlay.assignedMemberId }
      : {}),
    ...(overlay.assignedMember !== undefined ? { assignedMember: overlay.assignedMember } : {}),
  };
}

export function applyConversationMutationOverlays(
  items: ConversationListItemDto[],
  overlays: ReadonlyMap<number, ConversationMutationOverlay>,
  responseEpoch: number,
): ConversationListItemDto[] {
  if (overlays.size === 0) {
    return items;
  }
  return items.map((item) =>
    applyConversationMutationOverlay(item, overlays.get(item.conversationId), responseEpoch),
  );
}

/** Overlay a confirmed mutation onto a detail or thread response from an older epoch. */
export function applyConversationDetailsMutationOverlay<
  T extends {
    status: ConversationStatusName;
    assignedMemberId: number | null;
    assignedMember: ConversationListItemDto["assignedMember"];
  },
>(details: T, overlay: ConversationMutationOverlay | undefined, responseEpoch: number): T {
  if (!overlay || overlay.epoch <= responseEpoch) {
    return details;
  }
  return {
    ...details,
    ...(overlay.status !== undefined ? { status: overlay.status } : {}),
    ...(overlay.assignedMemberId !== undefined
      ? { assignedMemberId: overlay.assignedMemberId }
      : {}),
    ...(overlay.assignedMember !== undefined ? { assignedMember: overlay.assignedMember } : {}),
  };
}

/**
 * Whether a row still belongs in the active list view.
 * MINE without a known viewer id stays (the client cannot prove a mismatch).
 */
export function conversationMatchesListView(
  item: { status: ConversationStatusName; assignedMemberId: number | null },
  view: ConversationListViewName,
  viewerMemberId: number | null,
): boolean {
  switch (view) {
    case "ALL":
      return true;
    case "UNASSIGNED":
      return item.assignedMemberId == null;
    case "MINE":
      if (viewerMemberId == null) {
        return true;
      }
      return item.assignedMemberId === viewerMemberId;
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

/** Open includes PENDING. Closed includes legacy ARCHIVED. */
export function conversationMatchesListStatus(
  item: { status: ConversationStatusName },
  listStatus: ConversationListStatusName,
): boolean {
  switch (listStatus) {
    case "ALL":
      return true;
    case "OPEN":
      return item.status === "OPEN" || item.status === "PENDING";
    case "CLOSED":
      return item.status === "CLOSED" || item.status === "ARCHIVED";
    default: {
      const _exhaustive: never = listStatus;
      return _exhaustive;
    }
  }
}

export function conversationMatchesActiveList(
  item: { status: ConversationStatusName; assignedMemberId: number | null },
  view: ConversationListViewName,
  listStatus: ConversationListStatusName,
  viewerMemberId: number | null,
): boolean {
  return (
    conversationMatchesListView(item, view, viewerMemberId) &&
    conversationMatchesListStatus(item, listStatus)
  );
}
